import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Paper,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconArrowBackUp,
  IconPackageExport,
  IconPackageImport,
  IconUser,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { _localization } from "../../config/location";

/**
 * Xem chi tiết phiếu nhập kho bảo hành (NHAP_KHO_BAO_HANH) của luồng Dịch vụ sửa chữa.
 *
 * Phiếu này do consumer tự sinh khi lưu phiếu tiếp nhận nên không có màn tạo mới;
 * bố cục ở đây bám theo đúng các màn nhập/xuất kho khác của module để nhất quán.
 */
const ServiceRepairInWardWarranty = ({
  back,
  dataRowProps,
  detail,
}: {
  back: any;
  dataRowProps: any;
  detail: any;
}) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      setHeight(window.innerHeight - (185 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "itemCode",
        header: "Thông tin sản phẩm",
        Cell: ({ row }) => (
          <Box w={250}>
            <Text size="12.5px" c="black">
              {row.original.itemCode}
            </Text>
            <Tooltip label={row.original.itemName} multiline w={250}>
              <Text size="12px" truncate="end" c="dimmed">
                {row.original.itemName}
              </Text>
            </Tooltip>
          </Box>
        ),
        size: 300,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "serial",
        header: "Serial",
        size: 150,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "serialStatus",
        header: "Trạng thái serial",
        size: 130,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "itemSource",
        header: "Nguồn hàng",
        size: 120,
        Cell: ({ row }) =>
          row.original.itemSource === "HANG_NGOAI"
            ? "Hàng ngoài"
            : row.original.itemSource === "HANG_HACOM"
              ? "Hàng HACOM"
              : (row.original.itemSource ?? ""),
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "quantity",
        header: "Số lượng",
        size: 90,
        Cell: ({ row }) => (
          <Text size="12.5px">
            {row.original.quantity ?? 1} {row.original.uomCode ?? ""}
          </Text>
        ),
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "postionCode",
        header: "Vị trí nhập",
        size: 150,
        Cell: ({ row }) =>
          row.original.postionCode && row.original.positionname
            ? `${row.original.postionCode} - ${row.original.positionname}`
            : (row.original.postionCode ?? ""),
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "note",
        header: "Ghi chú",
        size: 300,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
    ],
    [],
  );

  const table = useMantineReactTable({
    columns,
    data: dataRowProps ?? [],
    positionToolbarAlertBanner: "bottom",
    enableTopToolbar: false,
    renderTopToolbarCustomActions: () => <></>,
    renderToolbarInternalActions: () => <></>,
    localization: _localization,
    enableRowSelection: false,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: { left: ["itemCode"], right: [] },
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    mantineTableBodyCellProps: {
      style: { fontSize: "12.5px", padding: "5px 15px" },
    },
    enablePagination: false,
    enableBottomToolbar: false,
    enableColumnPinning: true,
    mantineTableProps: { striped: false },
    columnFilterDisplayMode: "popover",
  });

  const branchName =
    detail?.branchCode && detail?.branchName
      ? `${detail.branchCode} - ${detail.branchName}`
      : (detail?.branchName ?? "");

  const inventoryName =
    detail?.inventoryCode && detail?.inventoryName
      ? `${detail.inventoryCode} - ${detail.inventoryName}`
      : (detail?.inventoryName ?? "");

  const creatorName =
    detail?.creatorCode && detail?.creatorName
      ? `${detail.creatorCode} - ${detail.creatorName}`
      : (detail?.creatorName ?? "");

  return (
    <Box>
      <Box ref={headerRef}>
        <Group
          justify="space-between"
          style={{ position: "sticky", bottom: 0, backgroundColor: "white" }}
        >
          <Badge
            variant="gradient"
            gradient={{ from: "blue", to: "cyan", deg: 90 }}
          >
            {`Chi tiết phiếu nhập kho bảo hành${detail?.transactionCode ? ` - ${detail.transactionCode}` : ""}`}
          </Badge>
          <Flex gap="md">
            <Button
              type="button"
              variant="default"
              size="sm"
              radius="md"
              leftSection={<IconArrowBackUp size={16} stroke={1.5} />}
              onClick={() => back(false)}
            >
              Quay lại
            </Button>
          </Flex>
        </Group>

        <Grid mt={5} gutter="md" mb="sm">
          {/* ===== CỘT TRÁI: PHIẾU DỊCH VỤ & KHÁCH HÀNG ===== */}
          <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
            <Paper
              p="md"
              radius="md"
              withBorder
              style={{
                height: "100%",
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <Group
                gap="xs"
                mb="sm"
                pb="xs"
                style={{ borderBottom: "1px solid #f1f5f9" }}
              >
                <ThemeIcon color="blue" variant="light" size="md" radius="sm">
                  <IconPackageExport size={18} />
                </ThemeIcon>
                <Text fw={600} size="sm" style={{ color: "#1e293b" }}>
                  Phiếu dịch vụ & khách hàng
                </Text>
              </Group>
              <Grid gutter="xs">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Số phiếu tiếp nhận"
                    size="xs"
                    value={detail?.receiveTicketCode ?? detail?.sourceCode ?? ""}
                    variant="filled"
                    readOnly
                    fw={600}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Tên khách hàng"
                    size="xs"
                    value={detail?.customerName ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Số điện thoại"
                    size="xs"
                    value={detail?.customerPhone ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Tỉnh / Thành phố"
                    size="xs"
                    value={detail?.provinceName ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Textarea
                    label="Địa chỉ"
                    size="xs"
                    value={detail?.customerAddress ?? ""}
                    variant="filled"
                    readOnly
                    minRows={1}
                    autosize
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Textarea
                    label="Ghi chú"
                    size="xs"
                    value={detail?.description ?? ""}
                    variant="filled"
                    readOnly
                    minRows={2}
                    autosize
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          </Grid.Col>

          {/* ===== CỘT PHẢI: THÔNG TIN NHẬP KHO ===== */}
          <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
            <Paper
              p="md"
              radius="md"
              withBorder
              style={{
                height: "100%",
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <Group
                gap="xs"
                mb="sm"
                pb="xs"
                style={{ borderBottom: "1px solid #f1f5f9" }}
              >
                <ThemeIcon color="teal" variant="light" size="md" radius="sm">
                  <IconPackageImport size={18} />
                </ThemeIcon>
                <Text fw={600} size="sm" style={{ color: "#1e293b" }}>
                  Thông tin nhập kho
                </Text>
              </Group>
              <Grid gutter="xs">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Mã phiếu"
                    size="xs"
                    value={detail?.transactionCode ?? ""}
                    variant="filled"
                    readOnly
                    fw={600}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <DatePickerInput
                    label="Ngày nhập"
                    size="xs"
                    value={detail?.createDate ? new Date(detail.createDate) : null}
                    valueFormat="DD-MM-YYYY"
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Chi nhánh"
                    size="xs"
                    value={branchName}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Kho"
                    size="xs"
                    value={inventoryName}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Tên người nhận"
                    size="xs"
                    value={creatorName}
                    variant="filled"
                    readOnly
                    leftSection={<IconUser size={14} />}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    label="Bộ phận"
                    size="xs"
                    value={detail?.departmentName ?? ""}
                    variant="filled"
                    readOnly
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          </Grid.Col>
        </Grid>
      </Box>

      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </Box>
  );
};

export default ServiceRepairInWardWarranty;
